import { io } from 'socket.io-client';

import type { MessagePayload } from '../types/ChatWindow';
import type { IOBundle, IOEvent } from '../types/io';
import {
  default as localStorageService,
  default as storage,
} from './localStorageService';

type IO = ReturnType<typeof io>;

let IOInstance: IO | null = null;
let initialUri: string;
let initialBotId: string;

const ioConfig = {
  path: '/sockets',
  transports: ['websocket'],
};

const init = async (
  uri: string,
  botId: string,
  onRegisterEvent: () => void,
) => {
  initialUri = uri;
  initialBotId = botId;
  if (!IOInstance) {
    console.log(`Initializing IO service for ${botId}`);
    IOInstance = io(`${uri}/web-client`, {
      query: {
        id: storage.get('id'),
        'conversed-bot': botId,
      },
      ...ioConfig,
    });

    // Wait for the socket to connect and get the id
    IOInstance.on('connect', async () => {
      const id = await getChatBotUserId();
      if (id) {
        send({
          event: 'id',
          data: { id, type: 'id', newId: { id: getSocketID() } },
        });
      }

      onRegisterEvent();
    });
  } else {
    // If the websocket connection is already established, just register the events again ( example: after closing and re-opening the chat window )
    onRegisterEvent();
  }
};

const getChatBotUserId = async () => {
  if (localStorageService.get('id')) {
    return localStorageService.get('id');
  }

  return null;
};

const registerEvent = ({ event, callback }: IOEvent) => {
  IOInstance?.on(event, (payload: MessagePayload) => {
    const direction = payload?.type === 'system' ? 'system' : 'out';
    callback(direction, payload);
  });
};

const send = ({ event, data }: IOBundle) => {
  IOInstance?.emit(event, data);
};

const disconnect = () => {
  IOInstance?.disconnect();
};

const reInitialize = (onRegisterEvent: () => void) => {
  disconnect();
  IOInstance = null;
  init(initialUri, initialBotId, onRegisterEvent);
};

const removeListeners = () => {
  IOInstance?.removeAllListeners();
};

export function getSocketID() {
  return IOInstance?.id;
}

export default {
  init,
  reInitialize,
  registerEvent,
  send,
  disconnect,
  removeListeners,
};
